import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, ArrowUpRightIcon, ChevronDownIcon, ClockIcon, MailIcon, MenuIcon, PhoneIcon, XIcon } from 'lucide-react';
import { Logo } from './Logo';
import { clinic, mainNav } from '../../data/clinic';
import type { NavGroup, NavLinkItem } from '../../data/clinic';
const EASE = [0.23, 1, 0.32, 1] as const;
export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const {
    pathname
  } = useLocation();
  const closeTimer = useRef<number>();
  useEffect(() => {
    setMobileOpen(false);
    setOpenMenu(null);
  }, [pathname]);

  // Lock page scroll while the mobile menu is open; close it if the viewport grows to desktop.
  useEffect(() => {
    if (!mobileOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const mq = window.matchMedia('(min-width: 1280px)');
    const onChange = () => mq.matches && setMobileOpen(false);
    mq.addEventListener('change', onChange);
    return () => {
      document.body.style.overflow = previous;
      mq.removeEventListener('change', onChange);
    };
  }, [mobileOpen]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpenMenu(null);
        setMobileOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  const open = (label: string) => {
    window.clearTimeout(closeTimer.current);
    setOpenMenu(label);
  };
  const scheduleClose = () => {
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpenMenu(null), 140);
  };
  const itemClass = (active: boolean) => `inline-flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-2 text-[15px] font-medium transition-colors duration-150 ${active ? 'text-primary' : 'text-ink hover:text-primary'}`;
  const groupActive = (children: NavLinkItem[]) => children.some(c => !c.external && pathname.startsWith(c.to));
  return <header className="sticky top-0 z-40 bg-white" onMouseLeave={scheduleClose} data-id="element-201" data-mp="3edytpdznb6ec">
      <div className="hidden h-10 bg-ink text-white xl:block" data-id="element-202" data-mp="3ebvt600v9uvd">
        <div className="mx-auto flex h-full max-w-7xl items-center justify-between px-8 text-[13px]" data-id="element-203" data-mp="2qp78j6kh3ob2">
          <div className="flex items-center gap-6 text-white/75" data-id="element-204" data-mp="2mo3rigqovm89">
            <span className="flex items-center gap-2" data-id="element-205" data-mp="30eqsygnu3q81">
              <ClockIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-206" data-mp="30fifz33d8fmp" />
              {clinic.hours}
            </span>
            <a href={`mailto:${clinic.email}`} className="flex items-center gap-2 hover:text-white" data-id="element-207" data-mp="2vlnupv843sli">
              <MailIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-208" data-mp="22ytdr8ai7bcs" />
              {clinic.email}
            </a>
          </div>
          <div className="flex items-center gap-6" data-id="element-209" data-mp="ig97bzvcls93">
            <Link to="/herhaalmedicatie" className="text-white/75 hover:text-white" data-id="element-210" data-mp="ieo8hy203n5f">
              Herhaalmedicatie
            </Link>
            <Link to="/vacatures" className="text-white/75 hover:text-white" data-id="element-211" data-mp="9gci9x51qrdi">
              Vacatures
            </Link>
            <a href={clinic.phoneHref} className="flex items-center gap-2 font-semibold text-cyan hover:text-white" data-id="element-212" data-mp="eg8q67gv72yc">
              <PhoneIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-213" data-mp="1259rlara5du4" />
              {clinic.phone}
            </a>
          </div>
        </div>
      </div>

      <div className="relative border-b border-line" data-id="element-214" data-mp="s6u9tye57gxa">
        <div className="mx-auto flex h-[76px] max-w-7xl items-center justify-between gap-6 px-5 lg:px-8" data-id="element-215" data-mp="s6c66gp5i0fc">
          <Link to="/" aria-label="Excellent Care Clinics, naar de homepage" className="shrink-0" data-id="element-216" data-mp="x3u99qghllff">
            <Logo data-id="element-217" data-mp="3cyw4oj11n30e" />
          </Link>

          <nav aria-label="Hoofdmenu" className="hidden xl:block" data-id="element-218" data-mp="3hvmggu5tfcg0">
            <ul className="flex items-center gap-1" data-id="element-219" data-mp="3httbdjaup0a4">
              {mainNav.map(item => <li key={item.label} className="relative" onMouseEnter={() => item.children ? open(item.label) : scheduleClose()} data-id="element-220" data-mp="129j8n1kuiy0">
                  {item.children ? <>
                    <button type="button" aria-expanded={openMenu === item.label} aria-haspopup="true" onClick={() => setOpenMenu(openMenu === item.label ? null : item.label)} className={itemClass(groupActive(item.children) || openMenu === item.label)} data-id="element-221" data-mp="3jffr07fmxqwi">
                      {item.label}
                      <ChevronDownIcon className={`h-4 w-4 transition-transform duration-150 ${openMenu === item.label ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-222" data-mp="5yzzpbhangjy" />
                    </button>
                    <AnimatePresence data-id="element-223" data-mp="2gmbn5aq0v6bg">
                      {openMenu === item.label && <DropdownPanel group={item} onEnter={() => open(item.label)} data-id="element-224" data-mp="2lkmw7j8htcsm" />}
                    </AnimatePresence>
                    </> : item.external ? <a href={item.to} target="_blank" rel="noreferrer" className={itemClass(false)} data-id="element-225" data-mp="2qhvfjn5dnmvg">
                      {item.label}
                      <ArrowUpRightIcon className="h-3.5 w-3.5 text-muted" aria-hidden="true" data-id="element-226" data-mp="2zcjnxm691koh" />
                    </a> : <NavLink to={item.to ?? '/'} className={({
                isActive
              }) => itemClass(isActive)} data-id="element-227" data-mp="349a0hziuhy55">
                      {item.label}
                    </NavLink>}
                </li>)}
            </ul>
          </nav>

          <Link to="/contact" onMouseEnter={scheduleClose} className="hidden whitespace-nowrap rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-primary-dark xl:inline-flex" data-id="element-228" data-mp="1ewpz106kmfgw">
            Contact
          </Link>

          <button type="button" className="flex h-10 w-10 items-center justify-center rounded-lg text-ink hover:bg-surface xl:hidden" aria-expanded={mobileOpen} aria-controls="mobiel-menu" aria-label={mobileOpen ? 'Menu sluiten' : 'Menu openen'} onClick={() => setMobileOpen(v => !v)} data-id="element-229" data-mp="16wk12jixqy4u">
            {mobileOpen ? <XIcon className="h-5 w-5" data-id="element-230" data-mp="1pv4q8619ntfu" /> : <MenuIcon className="h-5 w-5" data-id="element-231" data-mp="1pttla2w34fn5" />}
          </button>
        </div>
      </div>

      <AnimatePresence data-id="element-232" data-mp="24hxwklst5ovw">
        {mobileOpen && <motion.nav id="mobiel-menu" aria-label="Mobiel menu" initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.2,
        ease: EASE
      }} className="absolute inset-x-0 top-full max-h-[calc(100dvh-76px)] overflow-y-auto overscroll-contain border-b border-line bg-white px-5 pb-6 pt-2 shadow-lg sm:px-8 xl:hidden" data-id="element-233" data-mp="1zk62bl4oxqoj">
            <ul className="divide-y divide-line" data-id="element-234" data-mp="2x4xtyamsegwy">
              {mainNav.map(item => item.children ? <li key={item.label} className="py-3" data-id="element-235" data-mp="2o510lvpgctvk">
                    <p className="py-1 text-xs font-semibold uppercase tracking-wider text-muted" data-id="element-236" data-mp="2o3hn2cv6kwxh">{item.label}</p>
                    <ul className="mt-1 grid grid-cols-1 gap-x-4 min-[400px]:grid-cols-2" data-id="element-237" data-mp="29etqc39lmh5y">
                      {item.children.map(child => <li key={child.label} data-id="element-238" data-mp="20fzyeq77qeyq">
                          <MenuLink item={child} className="flex min-h-[44px] items-center gap-1 py-2 text-[15px] text-ink" data-id="element-239" data-mp="20hb3qugb3ury" />
                        </li>)}
                    </ul>
                  </li> : <li key={item.label} data-id="element-240" data-mp="1vgwtd5r8g8qr">
                    {item.external ? <a href={item.to} target="_blank" rel="noreferrer" className="flex items-center gap-1 py-3.5 text-base font-medium text-ink" data-id="element-241" data-mp="2ayel9gvwvc7k">
                        {item.label}
                        <ArrowUpRightIcon className="h-4 w-4 text-muted" aria-hidden="true" data-id="element-242" data-mp="25xh8xkxl1cdz" />
                      </a> : <NavLink to={item.to ?? '/'} className={({
              isActive
            }) => `block py-3.5 text-base font-medium ${isActive ? 'text-primary' : 'text-ink'}`} data-id="element-243" data-mp="1wxkcuy8v5b8w">
                        {item.label}
                      </NavLink>}
                  </li>)}
            </ul>
            <div className="mt-4 grid grid-cols-2 gap-3" data-id="element-244" data-mp="1e8x9qla8yvxy">
              <a href={clinic.phoneHref} className="flex items-center justify-center gap-2 rounded-full border border-line py-3 text-sm font-semibold text-ink" data-id="element-245" data-mp="1e6b1ejfj8ceo">
                <PhoneIcon className="h-4 w-4 text-primary" aria-hidden="true" data-id="element-246" data-mp="3fxa7x2jiomt9" />
                Bellen
              </a>
              <Link to="/contact" className="rounded-full bg-primary py-3 text-center text-sm font-semibold text-white" data-id="element-247" data-mp="3kvv66wf7kwf2">
                Contact
              </Link>
            </div>
          </motion.nav>}
      </AnimatePresence>
    </header>;
}
function DropdownPanel({
  group,
  onEnter
}: {
  group: NavGroup;
  onEnter: () => void;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: -4,
    scale: 0.98
  }} animate={{
    opacity: 1,
    y: 0,
    scale: 1
  }} exit={{
    opacity: 0,
    y: -4,
    scale: 0.98
  }} transition={{
    duration: 0.15,
    ease: EASE
  }} onMouseEnter={onEnter} style={{
    transformOrigin: 'top left'
  }} className="absolute left-0 top-full z-50 pt-3" data-id="element-248" data-mp="2x4pgzqndors3">
      <div className="w-[360px] overflow-hidden rounded-2xl bg-white shadow-[0_24px_48px_-20px_rgba(3,59,74,0.3)] ring-1 ring-line" data-id="element-249" data-mp="2egu74huvzy49">
        <ul className="p-2" data-id="element-250" data-mp="2igcvxoptwtcd">
          {group.children?.map(child => <li key={child.label} data-id="element-251" data-mp="20voi97rh5s9v">
              <MenuLink item={child} className="group flex items-start justify-between gap-4 rounded-xl px-4 py-3 transition-colors duration-150 hover:bg-surface" rich data-id="element-252" data-mp="20wgbibxlndwz" />
            </li>)}
        </ul>
        {group.intro && <p className="border-t border-line bg-surface px-6 py-3.5 text-[13px] leading-snug text-muted" data-id="element-253" data-mp="2evzbuik65kyq">{group.intro}</p>}
      </div>
    </motion.div>;
}
function MenuLink({
  item,
  className,
  rich = false
}: {
  item: NavLinkItem;
  className: string;
  rich?: boolean;
}) {
  const content = rich ? <>
      <span data-id="element-254" data-mp="1r5lqfaw3akd4">
        <span className="block text-[15px] font-semibold text-ink group-hover:text-primary" data-id="element-255" data-mp="1r4txy8xsh2r2">{item.label}</span>
        <span className="mt-0.5 block text-[13px] leading-snug text-muted" data-id="element-256" data-mp="yi9491tucekc">{item.description}</span>
      </span>
      {item.external ? <ArrowUpRightIcon className="mt-1 h-4 w-4 shrink-0 text-muted group-hover:text-primary" aria-hidden="true" data-id="element-257" data-mp="13gamjkjg09ur" /> : <ArrowRightIcon className="mt-1 h-4 w-4 shrink-0 text-muted group-hover:text-primary" aria-hidden="true" data-id="element-258" data-mp="vx1p8po7o1m2" />}
    </> : <>
      {item.label}
      {item.external && <ArrowUpRightIcon className="h-3.5 w-3.5 text-muted" aria-hidden="true" data-id="element-259" data-mp="lyc50q8ue52n" />}
    </>;
  return item.external ? <a href={item.to} target="_blank" rel="noreferrer" className={className} data-id="element-260" data-mp="hxs9fr3x39im">
      {content}
    </a> : <Link to={item.to} className={className} data-id="element-261" data-mp="d11vpafl4q0d">
      {content}
    </Link>;
}